import { DateTime } from 'luxon';
import { memo, useState } from 'react';
import DatePicker, { ReactDatePickerProps, registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Arrow } from '../arrow';
import { InputLayout } from './input-layout';
import cn from 'classnames';
import { useTranslate } from '~/i18n';
import ar from 'date-fns/locale/ar-SA';
import { FieldError } from 'react-hook-form';

registerLocale('ar', ar);

interface Props extends Omit<ReactDatePickerProps, 'onChange'> {
   label?: React.ReactNode;
   labelClassName?: string;
   error?: FieldError | any;
   valid?: React.ReactNode;
   groupClassName?: string;
   name?: string;
   disabled?: boolean;
   description?: React.ReactNode;
   pickerRef?: any;
   onChange?: (
      date: Date | [Date | null, Date | null] | null,
      event: React.SyntheticEvent<any, Event> | undefined
   ) => any;
   selectsRange?: any;
   isRequired?: boolean;
   helperText?: React.ReactNode;
}

const Picker = ({
   label,
   description,
   name,
   labelClassName,
   error,
   valid,
   groupClassName,
   className,
   popperClassName,
   dateFormat,
   weekDayClassName,
   formatWeekDay,
   onChange,
   id,
   pickerRef,
   selectsRange,
   isRequired,
   helperText,
   ...props
}: Props) => {
   const [date, setDate] = useState<any>(null);
   const { lang } = useTranslate();

   return (
      <InputLayout
         id={id}
         groupClassName={cn(groupClassName, 'relative')}
         labelClassName={labelClassName}
         description={description}
         label={label}
         helperText={helperText}
         valid={valid}
         isRequired={isRequired}
         error={error}>
         <DatePicker
            id={id}
            name={name}
            autoComplete="off"
            selected={date}
            locale={lang}
            ref={pickerRef}
            selectsRange={!!selectsRange}
            showPopperArrow={false}
            disabledKeyboardNavigation
            className={cn(className, error && 'input__error', valid && 'input__valid', 'input')}
            dateFormat={dateFormat || 'EEEE, LLLL d'}
            popperClassName={cn(popperClassName, '!pt-0')}
            weekDayClassName={
               weekDayClassName ? weekDayClassName : () => 'uppercase !text-grey-300 text-xs'
            }
            formatWeekDay={
               formatWeekDay ? formatWeekDay : name => (lang === 'ar' ? name : name.substr(0, 3))
            }
            onChange={(d, e) => {
               setDate(d);
               onChange?.(d, e);
            }}
            renderCustomHeader={({
               decreaseYear,
               increaseYear,
               nextYearButtonDisabled,
               prevYearButtonDisabled,
               monthDate,
               decreaseMonth,
               increaseMonth,
               prevMonthButtonDisabled,
               nextMonthButtonDisabled,
            }) => {
               return (
                  <div className="flex px-3 pt-2">
                     <div className="flex flex-grow space-x-1 text-lg rtl:space-x-reverse ltr:text-left rtl:text-right">
                        <button type="button" className="flex items-center text-lg text-grey-200">
                           {DateTime.fromJSDate(monthDate).setLocale(lang).toFormat('LLLL yyyy')}
                           {/* <Arrow direction="right" className="transform scale-75 text-primary" /> */}
                        </button>
                        <div className="flex flex-col justify-center">
                           <button
                              type="button"
                              onClick={increaseYear}
                              disabled={nextYearButtonDisabled}>
                              <Arrow direction="up" className="h-2 text-primary" />
                           </button>
                           <button
                              type="button"
                              onClick={decreaseYear}
                              disabled={prevYearButtonDisabled}>
                              <Arrow direction="down" className="h-2 text-primary" />
                           </button>
                        </div>
                     </div>
                     <div className="flex items-center space-x-4 rtl:space-x-reverse">
                        <button
                           type="button"
                           onClick={decreaseMonth}
                           disabled={prevMonthButtonDisabled}>
                           <Arrow direction="left" className="text-primary" />
                        </button>
                        <button
                           type="button"
                           onClick={increaseMonth}
                           disabled={nextMonthButtonDisabled}>
                           <Arrow direction="right" className="text-primary" />
                        </button>
                     </div>
                  </div>
               );
            }}
            {...props}
         />
      </InputLayout>
   );
};

export default memo(Picker);
